import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Phone, Star, MapPin, ArrowRight, Sparkles, Scissors, HeartHandshake } from "lucide-react";
import { BUSINESS, SERVICE_CATEGORIES, WHY_POINTS, GALLERY_IMAGES, REVIEWS, FAQS, BUSINESS_STORY, HERO_IMAGE, HERO_IMAGE_SMALL } from "../data/business";
import { MaskedLines, Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { ServiceCard } from "../components/ServiceCard";
import { ReviewCard } from "../components/ReviewCard";
import { FAQAccordion } from "../components/FAQAccordion";
import { Marquee } from "../components/Marquee";
import { MapSection } from "../components/MapSection";
import { CTASection } from "../components/CTASection";
import { GalleryGrid } from "../components/GalleryGrid";
import { useSEO } from "../hooks/useSEO";

const WHY_ICONS = [Sparkles, MapPin, HeartHandshake];

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -40]);

  return (
    <section ref={ref} className="relative bg-ink grain overflow-hidden" data-testid="hero">
      <div className="absolute top-0 right-0 w-[40rem] h-[40rem] rounded-full bg-copper/10 blur-[120px]" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 md:pt-20 md:pb-28 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div style={{ y: yText }}>
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center gap-3 text-copper text-xs font-semibold tracking-[0.3em] uppercase mb-6"
          >
            <span className="h-px w-10 bg-copper" aria-hidden="true" />
            New Lookz Hair Salon
          </motion.p>
          <h1 className="font-display font-extrabold text-white leading-[1.02] text-[clamp(2.5rem,6vw,4.75rem)]">
            <MaskedLines
              lines={["Your Look.", "Your Style.", "Your New Lookz."]}
              delay={0.2}
              lineClassName=""
            />
          </h1>
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-6 text-white/60 text-base sm:text-lg leading-relaxed max-w-lg"
          >
            A modern local hair salon in Gandhi Nagar, Hyderabad, offering professional hair and
            grooming services in a convenient neighborhood location.
          </motion.p>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/book"
              data-testid="hero-book-btn"
              className="rounded-full bg-copper px-8 py-4 text-center font-display font-bold text-ink transition-all duration-300 hover:bg-copper-hover hover:-translate-y-0.5"
            >
              Book an Appointment
            </Link>
            <a
              href={BUSINESS.phoneHref}
              data-testid="hero-call-btn"
              className="rounded-full border border-white/25 px-8 py-4 font-display font-semibold text-white inline-flex items-center justify-center gap-2 transition-all duration-300 hover:border-copper hover:text-copper"
            >
              <Phone size={17} aria-hidden="true" /> Call {BUSINESS.phoneDisplay}
            </a>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yImg }} className="relative">
          <motion.div
            initial={reduce ? false : { clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-t-[10rem] rounded-b-xl overflow-hidden border border-copper/25 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)]"
          >
            <img
              src={HERO_IMAGE.src}
              alt={HERO_IMAGE.alt}
              width={1400}
              height={1750}
              fetchpriority="high"
              className="w-full h-[420px] sm:h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent" aria-hidden="true" />
          </motion.div>
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.05 }}
            className="absolute -bottom-6 -left-4 sm:-left-8 rounded-xl bg-paper px-5 py-4 shadow-[0_20px_50px_-20px_rgba(17,17,17,0.5)] flex items-center gap-3"
            data-testid="hero-rating-chip"
          >
            <span className="grid place-items-center w-10 h-10 rounded-full bg-copper/15 text-copper">
              <Star size={18} fill="currentColor" aria-hidden="true" />
            </span>
            <span>
              <span className="block font-display font-extrabold text-ink text-lg leading-none">{BUSINESS.googleRating}★</span>
              <span className="block text-xs text-mute mt-1">{BUSINESS.googleReviewCount} Google reviews</span>
            </span>
          </motion.div>
          <motion.img
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.15 }}
            src={HERO_IMAGE_SMALL.src}
            alt={HERO_IMAGE_SMALL.alt}
            loading="lazy"
            width={700}
            height={700}
            className="absolute -top-6 -right-3 sm:-right-6 w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-ink shadow-xl"
          />
        </motion.div>
      </div>
    </section>
  );
};

const TrustStrip = () => {
  const items = [
    { icon: Star, label: `${BUSINESS.googleRating}★ Google Rating` },
    { icon: null, label: `${BUSINESS.googleReviewCount} Google Reviews` },
    { icon: MapPin, label: BUSINESS.areaShort },
    { icon: null, label: "Open Most Days" },
  ];
  return (
    <section className="bg-ivory border-b border-ink/10" data-testid="trust-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((it, i) => (
          <Reveal key={it.label} delay={i * 0.06} className="flex items-center gap-3 justify-center lg:justify-start">
            {it.icon && <it.icon size={17} className="text-copper shrink-0" aria-hidden="true" />}
            <span className="font-display font-bold text-sm sm:text-base text-ink">{it.label}</span>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export default function Home() {
  useSEO(
    "New Lookz Hair Salon | Hair Salon in Gandhi Nagar, Hyderabad",
    "Visit New Lookz Hair Salon in New Bakaram, Gandhi Nagar, Hyderabad. Explore salon services, view the gallery, check hours and contact us for your next appointment.",
    "/"
  );

  return (
    <>
      <Hero />
      <TrustStrip />

      <section className="bg-ivory" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading
              eyebrow="What We Do"
              title="Made for Your Look"
              text="Draft service menu — final services and prices will be confirmed by the salon."
            />
            <Reveal delay={0.15}>
              <Link to="/services" data-testid="home-view-services" className="group inline-flex items-center gap-2 font-display font-bold text-sm text-ink border-b-2 border-copper pb-1 hover:text-copper transition-colors">
                View All Services <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICE_CATEGORIES.map((c, i) => <ServiceCard key={c.slug} category={c} index={i} />)}
          </div>
        </div>
      </section>

      <Marquee />

      <section className="bg-ink grain" data-testid="home-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <SectionHeading dark eyebrow="Why New Lookz" title="A Better Salon Experience Starts With the Details" />
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {WHY_POINTS.map((w, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <Reveal key={w.title} delay={i * 0.1} className="h-full">
                  <div className="h-full rounded-xl bg-coal border border-white/10 p-7 transition-all duration-500 hover:border-copper/40 hover:-translate-y-1">
                    <span className="font-editorial italic text-copper/70 text-lg">{String(i + 1).padStart(2, "0")}</span>
                    <span className="grid place-items-center w-11 h-11 rounded-full bg-copper/15 text-copper mt-3 mb-5">
                      <Icon size={19} aria-hidden="true" />
                    </span>
                    <h3 className="font-display font-bold text-white text-xl">{w.title}</h3>
                    <p className="mt-2.5 text-white/55 text-sm leading-relaxed">{w.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-ivory" data-testid="home-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <SectionHeading eyebrow="Gallery" title="Find Your Next Look" />
            <Reveal delay={0.15}>
              <Link to="/gallery" data-testid="home-view-gallery" className="group inline-flex items-center gap-2 font-display font-bold text-sm text-ink border-b-2 border-copper pb-1 hover:text-copper transition-colors">
                View Gallery <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
          <GalleryGrid images={GALLERY_IMAGES.slice(0, 6)} />
          <p className="mt-6 text-xs text-mute">Stock placeholder photography — to be replaced with authorized salon photos.</p>
        </div>
      </section>

      <section className="bg-paper border-y border-ink/10" data-testid="home-about">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="flex items-center gap-3 text-xs font-semibold tracking-[0.28em] uppercase text-copper mb-4">
              <span className="h-px w-8 bg-copper" aria-hidden="true" /> About
            </p>
            <h2 className="font-display font-extrabold text-ink leading-[1.08] text-[clamp(2rem,4vw,3.25rem)]">
              A Local Salon for Everyday Style
            </h2>
            <p className="mt-5 text-mute text-base sm:text-lg leading-relaxed">
              New Lookz Hair Salon is located in New Bakaram, Gandhi Nagar, serving customers from the
              surrounding Hyderabad area. We believe getting your hair done should be simple, comfortable
              and focused on the look you actually want.
            </p>
            <p className="mt-4 text-mute text-base leading-relaxed">
              Whether you're maintaining your regular style or trying something different, New Lookz
              provides a convenient local place to take care of your hair and grooming needs.
            </p>
            <blockquote className="mt-6 border-l-2 border-copper pl-5 font-editorial italic text-lg text-body/80" data-testid="home-business-story">
              {BUSINESS_STORY}
            </blockquote>
            <Link to="/about" data-testid="home-about-link" className="group mt-7 inline-flex items-center gap-2 font-display font-bold text-sm text-ink border-b-2 border-copper pb-1 hover:text-copper transition-colors">
              More About Us <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="rounded-xl overflow-hidden border border-ink/10 shadow-[0_30px_60px_-30px_rgba(17,17,17,0.4)]">
                <img
                  src="https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=1000&q=75"
                  alt="Contemporary salon interior with styling stations — stock placeholder"
                  loading="lazy"
                  width={1000}
                  height={750}
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -right-4 sm:-right-6 rounded-lg bg-ink text-white px-5 py-4 shadow-xl">
                <p className="font-display font-extrabold text-copper text-sm tracking-[0.15em]">NEW BAKARAM</p>
                <p className="text-xs text-white/60 mt-1 flex items-center gap-1.5"><Scissors size={12} aria-hidden="true" /> Gandhi Nagar, Hyderabad</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink grain" data-testid="home-reviews">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <SectionHeading dark eyebrow="Reviews" title="What Our Customers Say" text="Verified Google reviews are being collected — placeholders shown until then." />
            <Reveal delay={0.15}>
              <a href={BUSINESS.googleReviewsUrl} target="_blank" rel="noopener noreferrer" data-testid="home-google-reviews" className="group inline-flex items-center gap-2 font-display font-bold text-sm text-white border-b-2 border-copper pb-1 hover:text-copper transition-colors">
                View Google Reviews <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </a>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {REVIEWS.map((r, i) => <ReviewCard key={i} review={r} index={i} dark />)}
          </div>
        </div>
      </section>

      <MapSection />

      <section className="bg-ivory" data-testid="home-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <SectionHeading align="center" eyebrow="Good to Know" title="Questions Before Your Visit?" />
          <div className="mt-10">
            <FAQAccordion faqs={FAQS.slice(0, 5)} />
          </div>
          <Reveal className="text-center mt-8">
            <Link to="/faq" data-testid="home-view-faq" className="group inline-flex items-center gap-2 font-display font-bold text-sm text-ink border-b-2 border-copper pb-1 hover:text-copper transition-colors">
              All Questions <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
